import React, { useEffect, useState, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { APP } from "@/constants/testIds";
import {
  ArrowLeft, ArrowRight, Check, Upload, FileText, Trash2, Loader2,
  Sparkles, HelpCircle, Save, Clock, PartyPopper, SkipForward, Edit3,
  LineChart as LineChartIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import VoiceMic from "@/components/VoiceMic";

/* -------------------------------------------------------------------- */
/*  Section schema — with clear labels, placeholders, and helper hints  */
/* -------------------------------------------------------------------- */
const F = (name, label, opts = {}) => ({
  name, label,
  type: opts.type || "text",
  placeholder: opts.placeholder || "",
  hint: opts.hint || "",
  required: !!opts.required,
  options: opts.options,
  full: !!opts.full,   // full-width row
});

const SCHEMA = {
  founder: {
    title: "About You & Your Business",
    intro: "Let's start with the basics. This helps every AI agent personalize its guidance to you.",
    estMinutes: 3,
    fields: [
      F("full_name", "Your Full Name", { required: true, placeholder: "Jane Smith" }),
      F("business_name", "Business Name", { required: true, placeholder: "NovaGrid Analytics", hint: "The name customers know you by." }),
      F("business_email", "Business Email", { type: "email", placeholder: "jane@business.com" }),
      F("phone", "Phone Number", { type: "tel", placeholder: "(555) 123-4567" }),
      F("website", "Website", { type: "url", placeholder: "https://" }),
      F("city", "City", { placeholder: "Austin" }),
      F("state", "State / Province", { placeholder: "TX" }),
      F("country", "Country", { placeholder: "USA" }),
      F("years_in_business", "Years in Business", { type: "number", placeholder: "e.g. 2", hint: "Enter 0 if you haven't started yet." }),
      F("work_mode", "Are you working on this…", { type: "select", options: ["Full-Time", "Part-Time"] }),
      F("employees", "How many employees?", { type: "number", placeholder: "0" }),
      F("strength", "What's your biggest business strength?", { type: "textarea", full: true, placeholder: "e.g. Deep AI expertise, 20 years of industry relationships, a loyal customer base…", hint: "One or two sentences is plenty." }),
      F("challenge", "What's your biggest challenge right now?", { type: "textarea", full: true, placeholder: "e.g. Not enough leads, cash flow, hiring the right people…" }),
    ],
  },
  overview: {
    title: "Your Business at a Glance",
    intro: "Tell us what you do. If a friend asked at a dinner party, what would you say?",
    estMinutes: 3,
    fields: [
      F("one_sentence", "Describe your business in one sentence", { type: "textarea", full: true, required: true, placeholder: "We help small chambers of commerce run better entrepreneurship programs using AI.", hint: "Keep it human — no jargon needed." }),
      F("problem", "What problem do you solve?", { type: "textarea", full: true, placeholder: "Small chambers can't afford full-time program directors to coach entrepreneurs 1-on-1." }),
      F("products", "What do you sell?", { type: "textarea", full: true, placeholder: "Software subscriptions, coaching services, in-person workshops…" }),
      F("unique", "What makes you different?", { type: "textarea", full: true, placeholder: "The only AI advisor that remembers every conversation and every document forever." }),
      F("stage", "Which stage best describes your business?", { type: "select", required: true, options: ["Idea", "Just Getting Started", "Making Some Money", "Growing Steadily", "Scaling Fast", "Mature / Established"] }),
      F("structure", "Business structure", { type: "select", options: ["Sole Proprietor", "LLC", "Corporation", "Nonprofit", "Partnership", "Not sure yet"] }),
      F("year_founded", "Year founded", { type: "number", placeholder: "2024" }),
    ],
  },
  customers: {
    title: "Who Do You Serve?",
    intro: "The clearer you are about who your ideal customer is, the sharper your AI-generated plans will be.",
    estMinutes: 3,
    fields: [
      F("ideal_customer", "Describe your ideal customer", { type: "textarea", full: true, required: true, placeholder: "e.g. A director of an economic development office in a mid-sized U.S. city, budget $10K–$50K/year.", hint: "The person who NEEDS what you offer most." }),
      F("geography", "Where are they located?", { placeholder: "Texas, Southeast US, nationwide, global…" }),
      F("industries_served", "Industries you serve", { type: "textarea", full: true, placeholder: "Government agencies, universities, chambers of commerce…" }),
      F("pain_points", "Their top 3 pain points", { type: "textarea", full: true, placeholder: "1) Reporting to funders  2) Coaching capacity  3) Measuring impact" }),
      F("how_found", "How do they find you today?", { type: "textarea", full: true, placeholder: "Word of mouth, LinkedIn, industry conferences, referrals from partners…" }),
      F("customer_count", "How many active customers do you have?", { type: "number", placeholder: "0" }),
      F("avg_sale", "Average sale amount", { placeholder: "$1,200 / project, $99/month, etc." }),
    ],
  },
  revenue: {
    title: "The Money Picture",
    intro: "Rough numbers are fine — you can refine these anytime later.",
    estMinutes: 2,
    fields: [
      F("current_revenue", "Current annual revenue (last 12 months)", { placeholder: "$0 if pre-revenue" }),
      F("projected_revenue", "What do you think next year could look like?", { placeholder: "$" }),
      F("primary_sources", "Where does most of your revenue come from?", { type: "textarea", full: true, placeholder: "70% consulting, 30% software subscriptions." }),
      F("mrr", "Monthly recurring revenue (if any)", { placeholder: "$0" }),
      F("margins", "Rough gross margin %", { placeholder: "e.g. 60%" }),
      F("seasonality", "Any seasonality?", { type: "textarea", full: true, placeholder: "Q4 is our biggest quarter because…" }),
    ],
  },
  operations: {
    title: "How the Business Runs",
    intro: "Take a quick inventory of the tools and processes you use today.",
    estMinutes: 2,
    fields: [
      F("process", "Walk us through your typical customer journey", { type: "textarea", full: true, placeholder: "Lead comes in from LinkedIn → we book a call → we send a proposal → contract → onboarding → delivery." }),
      F("crm", "CRM (customer database)", { placeholder: "HubSpot, Salesforce, spreadsheet, none…" }),
      F("accounting", "Accounting software", { placeholder: "QuickBooks, Xero, Wave, none…" }),
      F("ai_tools", "What AI tools do you use today?", { placeholder: "ChatGPT, Claude, none yet…" }),
      F("hours_weekly", "Hours per week you work in the business", { type: "number", placeholder: "40" }),
      F("bottleneck", "What's the #1 bottleneck slowing you down?", { type: "textarea", full: true, placeholder: "e.g. Too much time on admin instead of selling." }),
    ],
  },
  financial: {
    title: "Money & Bookkeeping Readiness",
    intro: "Quick yes / no checkboxes — helps us assess capital readiness.",
    estMinutes: 2,
    fields: [
      F("has_pnl", "Do you have a Profit & Loss statement?", { type: "yesno" }),
      F("has_balance", "A Balance Sheet?", { type: "yesno" }),
      F("has_cashflow", "A Cash Flow statement?", { type: "yesno" }),
      F("has_bank", "A dedicated business bank account?", { type: "yesno" }),
      F("has_credit", "Business credit / credit card?", { type: "yesno" }),
      F("has_insurance", "Business insurance?", { type: "yesno" }),
      F("has_bookkeeper", "A bookkeeper?", { type: "yesno" }),
      F("has_cpa", "A CPA / accountant?", { type: "yesno" }),
      F("has_projections", "Financial projections?", { type: "yesno" }),
      F("desired_funding", "Are you looking for funding? If yes, how much?", { type: "textarea", full: true, placeholder: "$100K in Q1 for marketing and one hire." }),
    ],
  },
  marketing: {
    title: "Getting the Word Out",
    intro: "Where are you already showing up? Where would you like to grow?",
    estMinutes: 3,
    fields: [
      F("website", "Do you have a website live today?", { type: "yesno" }),
      F("gbp", "A Google Business Profile?", { type: "yesno" }),
      F("facebook", "Facebook page?", { placeholder: "facebook.com/…" }),
      F("instagram", "Instagram?", { placeholder: "@handle" }),
      F("linkedin", "LinkedIn?", { placeholder: "linkedin.com/…" }),
      F("youtube", "YouTube channel?", { placeholder: "url" }),
      F("email_list", "Size of your email list", { type: "number", placeholder: "0" }),
      F("monthly_budget", "Monthly marketing budget", { placeholder: "$500, $0, etc." }),
      F("goals", "Your top marketing goal for the next 12 months", { type: "textarea", full: true, placeholder: "Book 3 new enterprise clients per quarter." }),
    ],
  },
  sales: {
    title: "How You Sell",
    intro: "This helps us diagnose where deals are getting stuck.",
    estMinutes: 2,
    fields: [
      F("avg_leads", "Average leads per month", { type: "number", placeholder: "10" }),
      F("lead_sources", "Where do most leads come from?", { type: "textarea", full: true, placeholder: "Referrals, LinkedIn outreach, partner network, cold email…" }),
      F("conversion", "Rough conversion rate (lead → customer)", { placeholder: "e.g. 15%" }),
      F("cycle", "How long does a typical sale take?", { placeholder: "e.g. 3 weeks, 6 months" }),
      F("objections", "Top objections you hear", { type: "textarea", full: true, placeholder: "1) Too expensive  2) Not sure if we need it  3) We already use X" }),
      F("best_offer", "What's your best-performing offer today?", { placeholder: "The Growth Package at $499/month" }),
    ],
  },
  competition: {
    title: "The Competition",
    intro: "Understanding your competitors sharpens your positioning.",
    estMinutes: 2,
    fields: [
      F("top_five", "Name your top 3–5 competitors", { type: "textarea", full: true, placeholder: "Acme Co, Beta Inc, GammaAI…" }),
      F("why_you", "Why do customers pick YOU over them?", { type: "textarea", full: true, placeholder: "Faster response, deeper industry expertise, better price…" }),
      F("competitors_better", "What do competitors do better than you?", { type: "textarea", full: true, placeholder: "Larger sales team, more brand recognition…" }),
      F("you_better", "What do you do noticeably better?", { type: "textarea", full: true, placeholder: "Personalized service, no bureaucracy…" }),
    ],
  },
  growth: {
    title: "Where You're Headed",
    intro: "Dream big — this becomes the north star for your AI-generated business plan.",
    estMinutes: 2,
    fields: [
      F("revenue_1yr", "Revenue goal in 12 months", { placeholder: "$500K" }),
      F("revenue_3yr", "Revenue goal in 3 years", { placeholder: "$3M" }),
      F("revenue_5yr", "Revenue goal in 5 years", { placeholder: "$10M" }),
      F("hiring", "Who do you need to hire next?", { type: "textarea", full: true, placeholder: "First salesperson, marketing coordinator…" }),
      F("expansion", "New markets or products you're considering?", { type: "textarea", full: true, placeholder: "Expanding to Canada, launching a coaching program…" }),
      F("exit", "Do you have an exit strategy in mind?", { type: "textarea", full: true, placeholder: "Acquisition in 5–7 years, family succession, keep forever…" }),
    ],
  },
  documents: {
    title: "Upload Any Documents You Have",
    intro: "Attach anything relevant — business plans, financials, logos, etc. All optional. You can skip and add later.",
    estMinutes: 3,
    fields: [
      F("notes", "Anything else we should know?", { type: "textarea", full: true, placeholder: "A short note to Business Einstein — tell it what you'd like help with most." }),
    ],
    hasUploads: true,
  },
};

const ORDER = ["founder", "overview", "customers", "revenue", "operations", "financial", "marketing", "sales", "competition", "growth", "documents"];

const DOC_CATEGORIES = [
  { id: "business_plan", label: "Business Plan" },
  { id: "pitch_deck", label: "Pitch Deck" },
  { id: "financials", label: "Financial Statements" },
  { id: "logo", label: "Logo" },
  { id: "capability_statement", label: "Capability Statement" },
  { id: "brochure", label: "Brochure / Marketing" },
  { id: "strategic_plan", label: "Strategic Plan" },
  { id: "articles", label: "Articles of Organization" },
  { id: "tax_return", label: "Tax Returns" },
  { id: "insurance", label: "Insurance" },
  { id: "certification", label: "Certifications" },
  { id: "other", label: "Other" },
];

/* -------------------------------------------------------------------- */
/*  Main Component                                                      */
/* -------------------------------------------------------------------- */
export default function Onboarding() {
  const [dna, setDna] = useState(null);
  const [step, setStep] = useState(-1); // -1 = welcome; 0..10 = sections; 11 = done
  const [values, setValues] = useState({});
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const saveTimerRef = useRef(null);
  const firstLoad = useRef(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/dna");
      setDna(data.dna);
      setValues(data.dna?.sections || {});
      // Auto-jump to the next incomplete section (returning users)
      const completed = data.dna?.completed_sections || [];
      if (completed.length === 0) setStep(-1);
      else if (completed.length >= ORDER.length) setStep(ORDER.length);
      else {
        // Find first incomplete section index
        let nextIdx = 0;
        for (let i = 0; i < ORDER.length; i++) {
          if (!completed.includes(ORDER[i])) { nextIdx = i; break; }
        }
        setStep(nextIdx);
      }
    })();
  }, []);

  const sid = step >= 0 && step < ORDER.length ? ORDER[step] : null;
  const section = sid ? SCHEMA[sid] : null;
  const completed = dna?.completed_sections || [];
  const progress = Math.round((completed.length / ORDER.length) * 100);
  const remainingMinutes = ORDER.slice(step + 1).reduce((sum, k) => sum + (SCHEMA[k]?.estMinutes || 3), 0);

  const setField = (key, val) => {
    setValues((v) => ({ ...v, [sid]: { ...(v[sid] || {}), [key]: val } }));
    scheduleAutoSave();
  };

  const scheduleAutoSave = () => {
    setSaveState("idle");
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => save(true), 1200);
  };

  const save = async (silent = false) => {
    if (!sid) return;
    setSaveState("saving");
    try {
      const { data } = await api.post("/dna/section", { section_id: sid, data: values[sid] || {} });
      setDna((d) => ({ ...(d || {}), completed_sections: data.completed_sections }));
      setSaveState("saved");
      if (!silent) toast.success(`${section.title} saved`);
    } catch (e) {
      setSaveState("error");
      if (!silent) toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const next = async () => {
    await save(true);
    if (step < ORDER.length - 1) setStep(step + 1);
    else setStep(ORDER.length); // review page
  };

  const skip = () => {
    if (step < ORDER.length - 1) setStep(step + 1);
    else setStep(ORDER.length);
  };

  /* ---------- WELCOME COVER ---------- */
  if (step === -1) {
    return (
      <div className="mx-auto max-w-5xl">
        <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr]">
          <div className="rounded-xl border border-stone-200 bg-white p-10 shadow-sm">
            <div className="flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 w-fit">
              <Sparkles className="h-3 w-3 text-emerald-700" />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-800">Welcome to ATLAS™</span>
            </div>
            <h1 className="mt-6 font-display text-5xl font-bold leading-tight tracking-tight text-slate-950">
              Let's build your<br />Business DNA™.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-stone-700">
              We'll ask a few friendly questions about you and your business. The more you share,
              the smarter every AI recommendation becomes.
            </p>
            <div className="mt-8 grid gap-3 rounded-lg bg-stone-50 p-5">
              {[
                ["11 short sections", "About 25–35 minutes total. Take breaks — everything auto-saves."],
                ["Nothing is scary", "No jargon. If you don't know something, skip it and come back later."],
                ["No wrong answers", "Rough estimates are fine. You can edit anything, anytime."],
                ["Fully private", "Your data belongs to you. It powers your personal AI agents only."],
              ].map(([t, d]) => (
                <div key={t} className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-950 text-white">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-950">{t}</div>
                    <div className="text-xs leading-relaxed text-stone-600">{d}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button data-testid="onboarding-start" onClick={() => setStep(0)} className="btn-primary !px-8 !py-4 !text-base">
                Start with Section 1 <ArrowRight className="h-5 w-5" />
              </button>
              <div className="font-mono text-xs text-stone-500 flex items-center gap-1.5">
                <Clock className="h-3 w-3" /> ~30 minutes • Save & resume anytime
              </div>
            </div>
          </div>

          <UnlockPreview />
        </div>
      </div>
    );
  }

  /* ---------- REVIEW PAGE ---------- */
  if (step === ORDER.length) {
    const summary = ORDER.map((k) => {
      const s = SCHEMA[k];
      const vals = values[k] || {};
      const filled = s.fields.filter((f) => {
        const v = vals[f.name];
        return v !== undefined && v !== "" && v !== null;
      });
      return {
        key: k, title: s.title, total: s.fields.length, filled: filled.length,
        entries: filled.slice(0, 6).map((f) => ({ label: f.label, value: String(vals[f.name]) })),
      };
    });
    const overallFilled = summary.reduce((n, s) => n + s.filled, 0);
    const overallTotal = summary.reduce((n, s) => n + s.total, 0);

    return (
      <div className="mx-auto max-w-4xl">
        <div className="mb-6">
          <div className="label-mono mb-2">Almost Done — Review</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">See what you've told us so far.</h1>
          <p className="mt-2 text-sm text-stone-600">Take one last look. Click "Edit" on any section to make changes. When you're ready, hit "Complete Onboarding" — the AI agents get to work.</p>
        </div>

        <div className="mb-6 flex items-center gap-4 rounded-lg border border-stone-200 bg-white p-5">
          <div className="flex-1">
            <div className="label-mono">Overall Completeness</div>
            <div className="mt-1 text-sm text-stone-600">You've filled in <span className="font-medium text-slate-950">{overallFilled}</span> of {overallTotal} fields across 11 sections.</div>
          </div>
          <div className="font-display text-3xl font-bold text-emerald-600">{Math.round((overallFilled / Math.max(1, overallTotal)) * 100)}%</div>
        </div>

        <div className="space-y-4">
          {summary.map((s, i) => (
            <div key={s.key} className="rounded-lg border border-stone-200 bg-white p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">Section {i + 1}</div>
                  <div className="mt-1 font-display text-xl font-medium text-slate-950">{s.title}</div>
                  <div className="mt-1 font-mono text-xs text-stone-500">{s.filled} of {s.total} fields answered</div>
                </div>
                <button
                  data-testid={`review-edit-${s.key}`}
                  onClick={() => setStep(i)}
                  className="btn-secondary !text-xs !px-3 !py-2 flex-shrink-0"
                >
                  <Edit3 className="h-3.5 w-3.5" /> Edit
                </button>
              </div>
              {s.entries.length > 0 && (
                <div className="mt-4 grid gap-2 border-t border-stone-100 pt-4 md:grid-cols-2">
                  {s.entries.map((e, j) => (
                    <div key={j} className="text-xs">
                      <div className="font-mono text-[10px] uppercase text-stone-500">{e.label}</div>
                      <div className="mt-0.5 line-clamp-2 text-slate-950">{e.value}</div>
                    </div>
                  ))}
                </div>
              )}
              {s.entries.length === 0 && (
                <div className="mt-4 border-t border-stone-100 pt-4 text-xs italic text-stone-500">
                  Nothing filled in yet. It's optional — but every field improves your AI's accuracy.
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button data-testid="review-back" onClick={() => setStep(ORDER.length - 1)} className="btn-secondary !text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to Section 11
          </button>
          <button data-testid="review-finish" onClick={() => setStep(ORDER.length + 1)} className="btn-primary !px-8 !py-4 !text-base">
            Complete Onboarding <PartyPopper className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  /* ---------- CELEBRATION ---------- */
  if (step > ORDER.length) {
    return (
      <div className="mx-auto max-w-3xl">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-10 text-center">
          <PartyPopper className="mx-auto h-14 w-14 text-emerald-600" strokeWidth={1.5} />
          <h1 className="mt-6 font-display text-4xl font-bold text-emerald-950">You did it.</h1>
          <p className="mt-4 mx-auto max-w-xl text-base leading-relaxed text-emerald-900">
            Your Business DNA™ is complete. Every AI agent — Business Einstein, Health Score,
            Business Plan, Marketing Plan, Pitch Outliner and more — is now personalized to you.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/app/health-score" className="btn-primary">Generate my Business Health Score <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/app/einstein" className="btn-secondary">Talk to Business Einstein™</Link>
            <button onClick={() => setStep(0)} className="btn-ghost !text-xs">Review my answers</button>
          </div>
        </div>
      </div>
    );
  }

  /* ---------- STEP FORM ---------- */
  return (
    <div className="mx-auto max-w-4xl">
      {/* Sticky progress header */}
      <div className="sticky top-[57px] z-20 -mx-8 mb-6 border-b border-stone-200 bg-stone-50/95 px-8 py-4 backdrop-blur-xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">Business DNA™ Onboarding</div>
            <div className="mt-1 text-sm font-medium text-slate-950">
              Section {step + 1} of {ORDER.length} · {section.title}
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div className="hidden md:flex items-center gap-1.5 font-mono text-stone-500"><Clock className="h-3 w-3" /> ~{remainingMinutes} min left</div>
            <SaveIndicator state={saveState} />
          </div>
        </div>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-stone-200">
          <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Compact section dots (jump between sections) */}
      <div className="mb-6 flex flex-wrap items-center gap-1.5">
        {ORDER.map((k, i) => {
          const isDone = completed.includes(k);
          const isCurrent = i === step;
          return (
            <button
              key={k}
              onClick={() => setStep(i)}
              title={SCHEMA[k].title}
              data-testid={`dna-step-${i + 1}`}
              className={`group flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] transition-all ${isCurrent ? "border-slate-950 bg-slate-950 text-white" : isDone ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-stone-200 bg-white text-stone-500 hover:border-stone-400"}`}
            >
              {isDone && <Check className="h-3 w-3" strokeWidth={3} />}
              <span className="font-mono">{String(i + 1).padStart(2, "0")}</span>
            </button>
          );
        })}
      </div>

      {/* Section intro card */}
      <div className="rounded-xl border border-stone-200 bg-white p-8">
        <div className="mb-6">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-950">{section.title}</h2>
          <p className="mt-2 text-base leading-relaxed text-stone-600">{section.intro}</p>
        </div>

        {section.hasUploads && <DocumentUploader />}

        {/* Fields */}
        <div className="grid gap-6 md:grid-cols-2">
          {section.fields.map((f) => (
            <div key={f.name} className={f.full || f.type === "textarea" ? "md:col-span-2" : ""}>
              <label className="block">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-medium text-slate-950">
                    {f.label}
                    {f.required && <span className="ml-1 text-red-500">*</span>}
                  </span>
                  {!f.required && <span className="font-mono text-[10px] uppercase text-stone-400">optional</span>}
                </div>
                {f.hint && (
                  <div className="mt-1 flex items-start gap-1.5 text-xs text-stone-500">
                    <HelpCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                    <span>{f.hint}</span>
                  </div>
                )}
                <div className="mt-2">
                  {f.type === "select" ? (
                    <select value={(values[sid] || {})[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} className={inputBig}>
                      <option value="">Select…</option>
                      {f.options.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  ) : f.type === "yesno" ? (
                    <div className="flex gap-2">
                      {["Yes", "No"].map((o) => {
                        const val = (values[sid] || {})[f.name];
                        return (
                          <button
                            key={o} type="button" onClick={() => setField(f.name, o)}
                            className={`flex-1 rounded-md border px-4 py-3 text-sm font-medium transition-all ${val === o ? (o === "Yes" ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-stone-500 bg-stone-100 text-slate-950") : "border-stone-300 bg-white text-slate-700 hover:border-stone-400"}`}
                          >
                            {o}
                          </button>
                        );
                      })}
                    </div>
                  ) : f.type === "textarea" ? (
                    <div className="relative">
                      <textarea rows={3} placeholder={f.placeholder} value={(values[sid] || {})[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} className={`${inputBig} pr-12`} />
                      <VoiceMic onTranscript={(t) => setField(f.name, t)} testId={`mic-${sid}-${f.name}`} />
                    </div>
                  ) : (
                    <input type={f.type} placeholder={f.placeholder} value={(values[sid] || {})[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} className={inputBig} />
                  )}
                </div>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Nav */}
      <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          data-testid={APP.dnaBack}
          onClick={() => setStep(Math.max(-1, step - 1))}
          className="btn-secondary !text-sm"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          <button onClick={skip} className="btn-ghost !text-xs">
            <SkipForward className="h-3.5 w-3.5" /> Skip for now
          </button>
          <button data-testid={APP.dnaSave} onClick={() => save(false)} className="btn-ghost !text-xs">
            <Save className="h-3.5 w-3.5" /> Save
          </button>
          <button data-testid={APP.dnaNext} onClick={next} className="btn-primary !text-sm !px-6 !py-3">
            {step === ORDER.length - 1 ? (<>Review My Answers <ArrowRight className="h-4 w-4" /></>) : (<>Next <ArrowRight className="h-4 w-4" /></>)}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Reusable pieces                                                     */
/* -------------------------------------------------------------------- */
const inputBig = "w-full rounded-md border border-stone-300 bg-white px-4 py-3 text-base text-slate-950 outline-none transition-all placeholder:text-stone-400 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10";

function SaveIndicator({ state }) {
  if (state === "saving") return <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-stone-500"><Loader2 className="h-3 w-3 animate-spin" /> Saving…</span>;
  if (state === "saved") return <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-emerald-600"><Check className="h-3 w-3" /> Saved</span>;
  if (state === "error") return <span className="font-mono text-[10px] uppercase tracking-wider text-red-500">Save failed</span>;
  return <span className="font-mono text-[10px] uppercase tracking-wider text-stone-400">Auto-saves as you type</span>;
}

/* Small preview panel that shows sample AI outputs on the welcome screen */
function UnlockPreview() {
  const dims = ["Leadership", "Sales", "Marketing", "Finance", "Ops", "Team", "AI", "Funding"];
  const values = [8, 6, 7, 5, 6, 7, 9, 4]; // sample
  const size = 220, cx = size / 2, cy = size / 2, R = 80;
  const points = dims.map((_, i) => {
    const angle = (Math.PI * 2 * i) / dims.length - Math.PI / 2;
    const r = (values[i] / 10) * R;
    return `${cx + Math.cos(angle) * r},${cy + Math.sin(angle) * r}`;
  }).join(" ");
  const gridPoints = (frac) => dims.map((_, i) => {
    const angle = (Math.PI * 2 * i) / dims.length - Math.PI / 2;
    const r = frac * R;
    return `${cx + Math.cos(angle) * r},${cy + Math.sin(angle) * r}`;
  }).join(" ");

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-stone-200 bg-slate-950 p-6 text-white">
        <div className="flex items-center gap-2 text-emerald-400">
          <Sparkles className="h-3 w-3" />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em]">What you'll unlock</span>
        </div>
        <h3 className="mt-3 font-display text-xl font-medium">Your personal Business Health Score™</h3>
        <div className="mt-4 flex items-center justify-center">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="opacity-95">
            {[0.25, 0.5, 0.75, 1].map((f, i) => (
              <polygon key={i} points={gridPoints(f)} fill="none" stroke="#334155" strokeWidth="0.5" />
            ))}
            {dims.map((_, i) => {
              const angle = (Math.PI * 2 * i) / dims.length - Math.PI / 2;
              return <line key={i} x1={cx} y1={cy} x2={cx + Math.cos(angle) * R} y2={cy + Math.sin(angle) * R} stroke="#334155" strokeWidth="0.5" />;
            })}
            <polygon points={points} fill="#10b981" fillOpacity="0.25" stroke="#10b981" strokeWidth="2" />
            {dims.map((d, i) => {
              const angle = (Math.PI * 2 * i) / dims.length - Math.PI / 2;
              const x = cx + Math.cos(angle) * (R + 14), y = cy + Math.sin(angle) * (R + 14);
              return <text key={d} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fill="#94a3b8" fontSize="8" fontFamily="monospace">{d}</text>;
            })}
          </svg>
        </div>
        <div className="text-center font-mono text-[10px] uppercase tracking-wider text-stone-400">Sample — yours will be personalized</div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-6">
        <div className="flex items-center gap-2 text-slate-950">
          <LineChartIcon className="h-3.5 w-3.5" />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">Sample Business Plan Snippet</span>
        </div>
        <div className="mt-3 space-y-2 text-xs leading-relaxed text-slate-900">
          <p><span className="font-medium">Executive Summary.</span> <span className="text-stone-600">NovaGrid Analytics helps mid-sized US chambers of commerce deliver AI-powered coaching to their entrepreneur cohorts — replacing $200K in staff overhead with a single annual license.</span></p>
          <p><span className="font-medium">Market (SAM).</span> <span className="text-stone-600">1,300 chambers in the US serving 3.2M small businesses, growing 4.1% annually.</span></p>
          <p><span className="font-medium">Ask.</span> <span className="text-stone-600">$1.2M seed to expand from 12 to 100 chambers within 18 months.</span></p>
        </div>
        <div className="mt-4 border-t border-stone-100 pt-3 font-mono text-[10px] uppercase tracking-wider text-stone-400">
          Auto-generated when you finish your DNA
        </div>
      </div>
    </div>
  );
}

function DocumentUploader() {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState("business_plan");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const refresh = async () => {
    const { data } = await api.get("/uploads");
    setFiles(data.files || []);
  };
  useEffect(() => { refresh(); }, []);

  const onSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("File too large (20MB max)"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/uploads?doc_category=${category}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded`);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = async (id) => { await api.delete(`/uploads/${id}`); toast.success("Removed"); refresh(); };

  return (
    <div className="mb-8 rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 p-6">
      <div className="text-sm font-medium text-slate-950">Attach files (optional)</div>
      <p className="mt-1 text-xs text-stone-600">Business plan, pitch deck, logo, financials, tax returns — anything relevant. PDF, Word, Excel, PowerPoint, or images up to 20MB.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto]">
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputBig}>
          {DOC_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <div>
          <input ref={inputRef} data-testid="dna-file-input" type="file" onChange={onSelect} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.csv,.txt" />
          <button type="button" data-testid="dna-upload-btn" onClick={() => inputRef.current?.click()} disabled={uploading} className="btn-primary w-full">
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <><Upload className="h-4 w-4" /> Choose file</>}
          </button>
        </div>
      </div>
      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          {files.map((f) => (
            <div key={f.id} className="flex items-center justify-between rounded-md border border-stone-200 bg-white p-3">
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 flex-shrink-0 text-slate-950" strokeWidth={1.5} />
                <div>
                  <div className="text-sm font-medium text-slate-950">{f.original_filename}</div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500">
                    {f.doc_category.replace(/_/g, " ")} · {(f.size / 1024).toFixed(1)}KB
                  </div>
                </div>
              </div>
              <button onClick={() => remove(f.id)} className="btn-ghost !text-xs !text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
